import type React from 'react';
import { ContentType, Organization, Profile, Team } from '@/types/custom';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ROUTES } from '@/lib/constants';
import { assetBaseUrl, supabaseUrl } from '@/lib/constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Serve a Supabase Storage file from somewhere else, if we are pointed there.
 *
 * scripts/mirror-to-r2.py keeps a copy of both public buckets in Cloudflare R2
 * under the same keys, because Supabase's free plan gives 2 GB of egress a
 * month and R2 charges nothing for it. A public Supabase URL ends in
 * /<bucket>/<path>, so swapping hosts is pure string work -- no database
 * change, and clearing assetBaseUrl switches straight back.
 *
 * Anything not served from our own storage (a Dropbox recording, a local
 * asset) is left alone: the R2 copies of those sit under different keys and
 * moving to them means repointing the database, not rewriting a URL.
 */
export function assetUrl(url: string): string {
  if (!assetBaseUrl || !url) return url;
  const publicPrefix = `${supabaseUrl}/storage/v1/object/public/`;
  if (!url.startsWith(publicPrefix)) return url;
  return `${assetBaseUrl}/${url.slice(publicPrefix.length)}`;
}

/**
 * Fix Dropbox URLs for direct embedding (images/audio).
 * Replaces www.dropbox.com with dl.dropboxusercontent.com
 * to serve files directly without redirect chains that fail on mobile.
 *
 * Every image, recording and transcript the app loads passes through here, so
 * this is also where a file we host ourselves gets sent to its mirror.
 */
export function fixDropboxUrl(url: string | undefined | null): string {
  if (!url) return '';
  if (url.includes('www.dropbox.com') || url.includes('dl.dropboxusercontent.com')) {
    // Use raw=1 to serve files directly without redirect chains that fail in WKWebView
    let fixed = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');
    fixed = fixed.replace(/[?&]dl=[01]/, '');
    fixed += (fixed.includes('?') ? '&' : '?') + 'raw=1';
    return fixed;
  }
  return assetUrl(url);
}

/**
 * Normalize image/media URLs for Capacitor compatibility.
 * Handles relative URLs, protocol-relative URLs, and Dropbox URLs.
 * In Capacitor, relative URLs resolve to capacitor:// which breaks loading.
 */
/**
 * Fallback for an <img> that failed to load, retrying once before giving up.
 *
 * A dropped image used to be permanent: the handler swapped in the placeholder
 * and the artwork stayed missing until the page was reloaded. One retry covers
 * the blip a phone gets moving between cells or waking from sleep. The cache
 * buster matters -- without it the browser serves the failure straight back.
 */
export function retryImageOnce(
  event: React.SyntheticEvent<HTMLImageElement>,
  fallback = '/images/logotrans.svg',
): void {
  const img = event.currentTarget;
  if (img.dataset.retried === '1') {
    if (!img.src.endsWith(fallback)) img.src = fallback;
    return;
  }
  img.dataset.retried = '1';
  const url = img.src;
  if (!url || url.endsWith(fallback)) {
    img.src = fallback;
    return;
  }
  img.src = `${url}${url.includes('?') ? '&' : '?'}retry=1`;
}

export function normalizeImageUrl(url: string | undefined | null): string {
  if (!url) return '';

  let result: string;

  // Already a full URL — just fix Dropbox
  if (url.startsWith('http://') || url.startsWith('https://')) {
    result = fixDropboxUrl(url);
  }
  // Protocol-relative URL (//cdn.example.com/...)
  else if (url.startsWith('//')) {
    result = fixDropboxUrl(`https:${url}`);
  }
  // Relative Supabase storage path
  else if (url.startsWith('/storage/')) {
    result = assetUrl(`${supabaseUrl}${url}`);
  }
  // Other relative paths — leave as-is (local assets like /images/...)
  else {
    result = fixDropboxUrl(url);
  }

  return result;
}

export const formatTime = (timeInSec: number) => {
  const minutes = Math.floor(timeInSec / 60);
  const seconds = Math.floor(timeInSec % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format date to MM/DD/YYYY
export const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
};

export const getMostRecentMondayUTC = (): Date => {
  const now = new Date();
  const utcDate = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  );

  // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
  const dayOfWeek = utcDate.getUTCDay();

  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const monday = new Date(utcDate);
  monday.setUTCDate(utcDate.getUTCDate() - daysToSubtract);
  return monday;
};

/**
 * Extract first and last name from full name.
 * TODO: better to store separately in database.
 */
export const splitNames = (fullName: string): string[] => {
  const split = fullName.split(' ');
  if (split.length <= 1) return [fullName, ''];

  return [split[0], split[1]];
};

/**
 * Function to sort objects in a list by a given field (e.g. 'voice_name').
 */
export const sortObjectsByField = (a: any, b: any, field: string) => {
  if (a[field] < b[field]) return -1;
  if (a[field] > b[field]) return 1;
  return 0;
};

export interface TranscriptionWord {
  startTime: number;
  endTime: number;
  text: string;
}

export interface TranscriptionLine {
  index: number;
  startTime: number;
  endTime: number;
  text: string;
  words: TranscriptionWord[];
}

/**
 * Parse transcription - supports both JSON (with word-level timing) and SRT formats.
 */
export const parseTranscription = (transcription: string): TranscriptionLine[] => {
  const trimmed = transcription.trim();

  // Try JSON format first (starts with { or [)
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return parseJsonTranscription(trimmed);
    } catch (e) {
      console.error('Failed to parse JSON transcription:', e);
      return [];
    }
  }

  // Fall back to SRT format
  return parseSrtTranscription(transcription);
};

/**
 * Convert SRT-style timestamp (00:00:02,600) to seconds.
 */
const srtTimeToSeconds = (time: string): number => {
  const normalized = time.replace(',', '.');
  const [hours, minutes, seconds] = normalized.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Parse JSON transcription format.
 * Supports multiple formats:
 * - Array of { index, start, end, text } with SRT-style timestamps
 * - { segments: [{ start_time, end_time, text, words: [...] }] } with seconds
 */
/**
 * Split words into sentence-sized lines based on punctuation boundaries.
 * Falls back to ~8 word chunks if no punctuation found.
 */
const splitWordsIntoLines = (
  words: TranscriptionWord[],
  lineIndex: number,
): TranscriptionLine[] => {
  if (words.length === 0) return [];

  const lines: TranscriptionLine[] = [];
  let currentWords: TranscriptionWord[] = [];
  let idx = lineIndex;

  const flushLine = () => {
    if (currentWords.length === 0) return;
    lines.push({
      index: idx++,
      startTime: currentWords[0].startTime,
      endTime: currentWords[currentWords.length - 1].endTime,
      text: currentWords.map((w) => w.text).join(' '),
      words: [...currentWords],
    });
    currentWords = [];
  };

  for (const word of words) {
    currentWords.push(word);
    // Split on sentence-ending punctuation or after ~8 words with a comma/pause
    const endsWithPunctuation = /[.!?]$/.test(word.text);
    const endsWithComma = /[,;:]$/.test(word.text);
    if (endsWithPunctuation || (endsWithComma && currentWords.length >= 5) || currentWords.length >= 10) {
      flushLine();
    }
  }
  flushLine();

  return lines;
};

const parseJsonTranscription = (content: string): TranscriptionLine[] => {
  const json = JSON.parse(content);
  const segments = json.segments || json;

  const allLines: TranscriptionLine[] = [];
  let lineIdx = 0;

  for (const seg of segments) {
    // Handle both SRT-style timestamps ("00:00:02,600") and seconds (2.6)
    let startTime: number;
    let endTime: number;

    if (typeof seg.start === 'string') {
      startTime = srtTimeToSeconds(seg.start);
      endTime = srtTimeToSeconds(seg.end || seg.start);
    } else {
      startTime = seg.start_time || 0;
      endTime = seg.end_time || startTime;
    }

    // Parse word-level timing if available
    const words: TranscriptionWord[] = (seg.words || [])
      .filter((w: { text: string }) => w.text.trim() !== '')
      .map((w: { start_time: number; end_time: number; text: string }) => ({
        startTime: w.start_time,
        endTime: w.end_time,
        text: w.text.trim(),
      }));

    // If segment has many words, split into sentence-sized lines
    if (words.length > 10) {
      const splitLines = splitWordsIntoLines(words, lineIdx);
      allLines.push(...splitLines);
      lineIdx += splitLines.length;
    } else {
      allLines.push({
        index: lineIdx++,
        startTime,
        endTime,
        text: seg.text,
        words,
      });
    }
  }

  return allLines;
};

/**
 * Parse SRT format.
 * SRT timestamps use comma for milliseconds: 00:00:02,600 --> 00:00:06,460
 */
const parseSrtTranscription = (transcription: string): TranscriptionLine[] => {
  return transcription
    .split('\n\n')
    .map((block, idx) => {
      const lines = block.split('\n').filter((line) => line.trim() !== '');
      if (lines.length < 3) return null;

      const index = Number(lines[0]);
      const [start, end] = lines[1].split(' --> ');
      // Replace comma with dot for proper decimal parsing
      const normalizedStart = start.replace(',', '.');
      const normalizedEnd = end?.replace(',', '.') || normalizedStart;
      const [hours, minutes, seconds] = normalizedStart.split(':').map(Number);
      const [endHours, endMinutes, endSeconds] = normalizedEnd.split(':').map(Number);
      const startTime = hours * 3600 + minutes * 60 + seconds;
      const endTime = endHours * 3600 + endMinutes * 60 + endSeconds;
      const text = lines.slice(2).join(' ');

      return {
        index,
        startTime,
        endTime,
        text,
        words: [], // SRT doesn't have word-level timing
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
};

export const getAvatarUrl = (item: Team | Profile) => {
  if (item.avatar_url) return item.avatar_url;
  const name = 'name' in item ? item.name : item.naam;
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${name.replace(/ /g, '-')}`;
};

/**
 * Avatar that's less personal (better for teams and orgs).
 */
export const getIdenticon = (item: Organization | Team) => {
  if (item.avatar_url) return item.avatar_url;
  return `https://api.dicebear.com/7.x/identicon/svg?seed=${item.name.replace(/ /g, '-')}`;
};

/**
 * Generate and download an .ics calendar file for a scheduled activity.
 */
export function downloadIcs(
  title: string,
  scheduledAt: string,
  durationMinutes: number,
  options?: {
    variant?: 'team' | 'samen';
    activityType?: string;
  },
) {
  const start = new Date(scheduledAt);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

  const fmt = (d: Date) =>
    d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const baseUrl = window.location.origin;
  const variant = options?.variant;
  const activityType = options?.activityType;

  const appName = variant === 'samen' ? 'Van Hoofd naar Hart' : 'Hartzorg';
  const pageUrl = variant === 'samen'
    ? `${baseUrl}/samen?tab=samen-meditatie`
    : variant === 'team'
      ? `${baseUrl}/team?tab=activiteit`
      : baseUrl;

  const descParts: string[] = [];
  if (activityType) descParts.push(`Type: ${activityType}`);
  descParts.push(`Geplande activiteit via ${appName}`);
  descParts.push(`Open in de app: ${pageUrl}`);

  // ICS DESCRIPTION uses \\n for newlines
  const description = descParts.join('\\n');

  const summaryPrefix = appName;
  const summary = `${summaryPrefix} - ${title}`;

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${appName}//Activiteit//NL`,
    'BEGIN:VEVENT',
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `URL:${pageUrl}`,
    `UID:${crypto.randomUUID()}@hartzorg.app`,
    `DTSTAMP:${fmt(new Date())}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
